import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BarChart3,
  Crown,
  Download,
  FileText,
  Filter,
  Medal,
  Printer,
  Settings,
  TrendingUp,
  Upload,
  Users,
  XCircle,
  CheckCircle2,
  Percent,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ScatterChart,
  Scatter,
  ZAxis,
} from 'recharts';

import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import useBranchLabels from '../../hooks/useBranchLabels';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { useBulkDownload } from '../reports/useBulkDownload';
import { REPORT_DOCUMENT_TYPES } from '../../utils/reportDocumentTypes';
import { handleBlobDownloadResponse } from '../../utils/blobDownload';
import { isNativePlatform } from '../../utils/device';
import { getProfileAssetUrl } from '../../utils/api';
import { resultsEntryButtonLabel } from '../../utils/examResultsLabels';

const PIE_COLORS = ['#22c55e', '#ef4444'];

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '—';
  return (parts[0][0] || '').toUpperCase() + (parts[1]?.[0] || '').toUpperCase();
}

function fmtPct(n) {
  const v = Number(n || 0);
  if (Number.isNaN(v)) return '0%';
  return `${v.toFixed(v % 1 === 0 ? 0 : 2)}%`;
}

function statusBadge(status) {
  return status === 'pass'
    ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/20'
    : 'bg-rose-500/15 text-rose-600 border-rose-500/20';
}

function gradePill(grade) {
  if (!grade) return 'bg-muted text-muted-foreground';
  const g = String(grade).toUpperCase();
  if (g.includes('A')) return 'bg-blue-500/15 text-blue-600';
  if (g.includes('B')) return 'bg-indigo-500/15 text-indigo-600';
  if (g.includes('C')) return 'bg-amber-500/15 text-amber-700';
  return 'bg-muted text-muted-foreground';
}

export default function ExamResultsDashboard() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { user, canManageFeature } = useAuth();
  const { formatLabel } = useBranchLabels();
  const { toast } = useToast();
  const canManage = canManageFeature('exams');

  const schoolId = user?.school_id;

  const [loading, setLoading] = useState(true);
  const [exam, setExam] = useState(null);

  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [filters, setFilters] = useState({
    academicYearId: '',
    classId: '',
    sectionId: '',
    subjectId: '',
    status: 'active',
    annualAggregate: false,
  });

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      const raw = localStorage.getItem('exam_results_dashboard_settings');
      return raw ? JSON.parse(raw) : { showAttendanceChart: true, showGradeChart: true };
    } catch {
      return { showAttendanceChart: true, showGradeChart: true };
    }
  });

  const [payload, setPayload] = useState({
    stats: {
      total_students: 0,
      pass_students: 0,
      fail_students: 0,
      overall_pass_pct: 0,
      class_avg_pct: 0,
      highest_pct: 0,
      topper_name: '—',
    },
    charts: {
      pass_fail: [],
      subject_performance: [],
      grade_distribution: [],
      attendance_vs_performance: [],
    },
    toppers: [],
    table: [],
  });

  const [selectedIds, setSelectedIds] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRow, setPreviewRow] = useState(null);

  const { runBulk, progress, running: bulkRunning, error: bulkError, setError: setBulkError } = useBulkDownload(REPORT_DOCUMENT_TYPES.RESULT);

  const saveSettings = (next) => {
    setSettings(next);
    try {
      localStorage.setItem('exam_results_dashboard_settings', JSON.stringify(next));
    } catch {
      // ignore
    }
  };

  // Fetch exam meta
  useEffect(() => {
    if (!examId) return;
    setLoading(true);
    api.get(`/exams/${examId}`)
      .then((res) => setExam(res.data?.data || null))
      .catch(() => {
        setExam(null);
        toast({ title: 'Error', description: 'Failed to load exam', variant: 'destructive' });
      })
      .finally(() => setLoading(false));
  }, [examId, toast]);

  // Fetch filter lists
  useEffect(() => {
    if (!schoolId) return;
    api.get('/settings/academic-years', { params: { school_id: schoolId } })
      .then((res) => {
        const list = res.data?.data || [];
        setSessions(list);
        if (!filters.academicYearId && list.length) {
          const current = list.find((y) => y.is_current) || list[0];
          if (current) setFilters((p) => ({ ...p, academicYearId: String(current.id) }));
        }
      })
      .catch(() => setSessions([]));

    api.get('/classes', { params: { school_id: schoolId } })
      .then((res) => setClasses(res.data?.data || []))
      .catch(() => setClasses([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  useEffect(() => {
    if (!filters.classId) {
      setSections([]);
      setSubjects([]);
      setFilters((p) => ({ ...p, sectionId: '', subjectId: '' }));
      return;
    }
    api.get(`/classes/${filters.classId}`)
      .then((res) => setSections(res.data?.data?.sections || []))
      .catch(() => setSections([]));
  }, [filters.classId]);

  useEffect(() => {
    // Subjects derived from exam schedules for selected class (best match for this exam)
    if (!examId || !filters.classId) {
      setSubjects([]);
      return;
    }
    api.get(`/exams/${examId}/schedules`, { params: { class_id: filters.classId } })
      .then((res) => {
        const list = res.data?.data || [];
        const map = new Map();
        list.forEach((s) => {
          const sid = s.subject_id ?? s.subjectId;
          const name = s.subject_name ?? s.subjectName ?? s.subject?.name ?? s.subject ?? '';
          if (sid && name) map.set(String(sid), { id: String(sid), name });
        });
        setSubjects(Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => setSubjects([]));
  }, [examId, filters.classId]);

  const fetchAnalytics = useCallback(async () => {
    if (!examId) return;
    setBulkError?.('');
    setLoading(true);
    try {
      const params = {};
      if (schoolId) params.school_id = schoolId;
      if (filters.academicYearId) params.academic_year_id = Number(filters.academicYearId);
      if (filters.classId) params.class_id = Number(filters.classId);
      if (filters.sectionId) params.section_id = Number(filters.sectionId);
      if (filters.subjectId) params.subject_id = Number(filters.subjectId);
      if (filters.status) params.status = filters.status;
      // annualAggregate toggle reserved for future backend support; keep param for forward compatibility
      params.annual_aggregate = filters.annualAggregate ? 1 : 0;

      const res = await api.get(`/exams/${examId}/results/analytics`, { params });
      setPayload(res.data?.data || {});
      setSelectedIds([]);
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.message || 'Failed to load analytics', variant: 'destructive' });
      setPayload((p) => ({ ...p, table: [], toppers: [], charts: p.charts || {}, stats: p.stats || {} }));
      setSelectedIds([]);
    } finally {
      setLoading(false);
    }
  }, [examId, filters, schoolId, toast, setBulkError]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const rows = useMemo(() => payload?.table || [], [payload]);
  const allIds = useMemo(() => rows.map((r) => r.student_id).filter(Boolean), [rows]);
  const allSelected = selectedIds.length > 0 && allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));

  const toggleAll = (checked) => {
    setSelectedIds(checked ? [...allIds] : []);
  };
  const toggleOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const headerActionsDisabled = !exam || exam?.datesheet_status !== 'published';

  const publishAll = async () => {
    if (!canManage) return;
    if (!window.confirm('Publish results for all students? They will be notified.')) return;
    try {
      await api.post(`/exams/${examId}/publish-results`);
      toast({ title: 'Success', description: 'Results published' });
      fetchAnalytics();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.message || 'Publish failed', variant: 'destructive' });
    }
  };

  // Per-row publish intentionally not supported: scope is published-only analytics.
  const publishSingle = async () => {
    toast({ title: 'Not available', description: 'Publish happens at exam level from header/bulk.', variant: 'destructive' });
  };

  const exportPdf = async () => {
    if (!selectedIds.length) {
      toast({ title: 'Select students', description: 'Select at least 1 student from table for bulk export.', variant: 'destructive' });
      return;
    }
    try {
      await runBulk({
        studentIds: selectedIds,
        options: {
          academic_year_id: filters.academicYearId ? Number(filters.academicYearId) : undefined,
          exam_id: Number(examId),
          mode: 'exam',
        },
        output: 'merged',
      });
      toast({ title: 'Export ready', description: 'Merged PDF downloaded.' });
    } catch (e) {
      toast({ title: 'Export failed', description: e.message, variant: 'destructive' });
    }
  };

  const downloadSinglePdf = async (row) => {
    try {
      const res = await api.get('/reports/result/pdf', {
        params: {
          student_id: row.student_id,
          school_id: schoolId,
          academic_year_id: filters.academicYearId ? Number(filters.academicYearId) : undefined,
          exam_id: Number(examId),
          mode: 'exam',
        },
        responseType: 'blob',
      });
      await handleBlobDownloadResponse(
        res,
        'pdf',
        `result-${row.admission_no || row.student_id}.pdf`,
      );
    } catch (e) {
      toast({ title: 'Download failed', description: e?.response?.data?.message || e?.message || 'Failed', variant: 'destructive' });
    }
  };

  const printSingle = async (row) => {
    if (isNativePlatform()) {
      toast({
        title: 'Print not available',
        description: 'Use Download to save the result on mobile.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const res = await api.get('/reports/result/preview', {
        params: {
          student_id: row.student_id,
          school_id: schoolId,
          academic_year_id: filters.academicYearId ? Number(filters.academicYearId) : undefined,
          exam_id: Number(examId),
          mode: 'exam',
        },
      });
      const html = res.data?.data?.html || '';
      const w = window.open('', '_blank');
      if (!w) return;
      w.document.write(html);
      w.document.close();
      w.focus();
      w.print();
    } catch (e) {
      toast({ title: 'Print failed', description: e?.response?.data?.message || 'Failed', variant: 'destructive' });
    }
  };

  const openPreview = (row) => {
    setPreviewRow(row);
    setPreviewOpen(true);
  };

  const kpis = useMemo(() => {
    const s = payload?.stats || {};
    return [
      { label: 'Total Students', value: s.total_students ?? 0, icon: Users, tone: 'from-slate-900 to-slate-700' },
      { label: 'Pass Students', value: s.pass_students ?? 0, icon: CheckCircle2, tone: 'from-emerald-600 to-emerald-500' },
      { label: 'Failed Students', value: s.fail_students ?? 0, icon: XCircle, tone: 'from-rose-600 to-rose-500' },
      { label: 'Overall Pass %', value: fmtPct(s.overall_pass_pct ?? 0), icon: Percent, tone: 'from-sky-600 to-blue-500' },
      { label: 'Class Average', value: fmtPct(s.class_avg_pct ?? 0), icon: TrendingUp, tone: 'from-indigo-600 to-violet-500' },
      { label: 'Highest %', value: fmtPct(s.highest_pct ?? 0), icon: BarChart3, tone: 'from-amber-600 to-orange-500' },
      { label: 'Topper Name', value: s.topper_name || '—', icon: Crown, tone: 'from-fuchsia-600 to-pink-500' },
    ];
  }, [payload]);

  const passFailData = payload?.charts?.pass_fail || [];
  const subjectPerf = payload?.charts?.subject_performance || [];
  const gradeDist = payload?.charts?.grade_distribution || [];
  const attVsPerf = payload?.charts?.attendance_vs_performance || [];

  if (loading && !exam) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Exam not found.
        <div className="mt-4">
          <Button variant="outline" onClick={() => navigate('/exams')}>Back to exams</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Exam Results</h1>
          <p className="text-muted-foreground">Analyze, publish and manage student examination performance.</p>
          <p className="text-sm text-muted-foreground mt-1">{exam.name}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={publishAll}
            disabled={!canManage || headerActionsDisabled}
          >
            <Upload className="w-4 h-4 mr-2" />
            Publish Results
          </Button>
          <Button variant="outline" onClick={exportPdf} disabled={headerActionsDisabled || bulkRunning}>
            <FileText className="w-4 h-4 mr-2" />
            {bulkRunning ? 'Exporting…' : 'Export PDF'}
          </Button>
          <Button variant="outline" onClick={() => setSettingsOpen(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Result Settings
          </Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div
              key={k.label}
              className={`rounded-2xl p-[1px] bg-gradient-to-br ${k.tone} shadow-sm`}
            >
              <div className="rounded-2xl bg-white/90 dark:bg-background/80 backdrop-blur p-4 border border-white/10">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-muted-foreground">{k.label}</p>
                  <span className="rounded-xl bg-black/5 dark:bg-white/5 p-2">
                    <Icon className="w-4 h-4 text-foreground" />
                  </span>
                </div>
                <p className="text-xl font-semibold mt-2 text-foreground">{k.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="border-border bg-white dark:bg-background">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" /> Filters
          </CardTitle>
          {bulkError ? <p className="text-sm text-rose-600">{bulkError}</p> : null}
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
          <div className="space-y-2">
            <Label>Session</Label>
            <Select value={filters.academicYearId ? String(filters.academicYearId) : ''} onValueChange={(v) => setFilters((p) => ({ ...p, academicYearId: v }))}>
              <SelectTrigger><SelectValue placeholder="Academic year" /></SelectTrigger>
              <SelectContent>
                {sessions.map((y) => (
                  <SelectItem key={y.id} value={String(y.id)}>{y.name}{y.is_current ? ' (Current)' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Class</Label>
            <Select value={filters.classId ? String(filters.classId) : ''} onValueChange={(v) => setFilters((p) => ({ ...p, classId: v, sectionId: '', subjectId: '' }))}>
              <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
              <SelectContent>
                {classes.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{formatLabel(c.name, c)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Section</Label>
            <Select
              value={filters.sectionId ? String(filters.sectionId) : 'all'}
              onValueChange={(v) => setFilters((p) => ({ ...p, sectionId: v === 'all' ? '' : v }))}
              disabled={!filters.classId}
            >
              <SelectTrigger><SelectValue placeholder="All sections" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sections</SelectItem>
                {sections.map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>{formatLabel(s.name, s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Exam</Label>
            <div className="h-10 rounded-md border border-border bg-muted/30 px-3 flex items-center text-sm text-muted-foreground">
              {exam.name}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Subject</Label>
            <Select
              value={filters.subjectId ? String(filters.subjectId) : 'all'}
              onValueChange={(v) => setFilters((p) => ({ ...p, subjectId: v === 'all' ? '' : v }))}
              disabled={!filters.classId}
            >
              <SelectTrigger><SelectValue placeholder="All subjects" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All subjects</SelectItem>
                {subjects.map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>{formatLabel(s.name, s)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Student Status</Label>
            <Select value={filters.status || 'active'} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-3 lg:col-span-2">
            <Switch
              id="annual-aggregate"
              checked={Boolean(filters.annualAggregate)}
              onCheckedChange={(checked) => setFilters((p) => ({ ...p, annualAggregate: Boolean(checked) }))}
            />
            <Label htmlFor="annual-aggregate">Annual Aggregate Results</Label>
            <Badge variant="outline" className="ml-2">Preview</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-base">Pass vs Fail</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie dataKey="value" data={passFailData} innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {passFailData.map((entry, idx) => (
                    <Cell key={entry.name || idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <ReTooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-base">Subject Performance (Avg %)</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={subjectPerf.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="subject_name" tick={{ fontSize: 12 }} interval={0} angle={-15} height={55} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                <ReTooltip />
                <Bar dataKey="avg_pct" fill="#3b82f6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {settings.showGradeChart && (
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-base">Grade Distribution</CardTitle>
            </CardHeader>
            <CardContent className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={gradeDist}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="grade" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <ReTooltip />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {settings.showAttendanceChart && (
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-base">Attendance vs Performance</CardTitle>
            </CardHeader>
            <CardContent className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis type="number" dataKey="attendance_pct" name="Attendance" domain={[0, 100]} tick={{ fontSize: 12 }} />
                  <YAxis type="number" dataKey="percentage" name="Percentage" domain={[0, 100]} tick={{ fontSize: 12 }} />
                  <ZAxis type="number" range={[40, 160]} />
                  <ReTooltip cursor={{ strokeDasharray: '3 3' }} />
                  <Scatter data={attVsPerf} fill="#22c55e" />
                </ScatterChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Toppers */}
      <Card className="border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Crown className="w-5 h-5" /> Class Toppers
          </CardTitle>
          <Badge variant="outline" className="text-xs">Top 5</Badge>
        </CardHeader>
        <CardContent>
          {(!payload?.toppers || payload.toppers.length === 0) ? (
            <p className="text-sm text-muted-foreground">No topper data for current filters.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {payload.toppers.map((t, idx) => {
                const Icon = idx === 0 ? Crown : Medal;
                const photo = getProfileAssetUrl(t.avatar, `${t.student_id}-${idx}`) || '';
                return (
                  <div
                    key={t.student_id}
                    className="rounded-2xl border border-border bg-gradient-to-b from-white to-muted/20 p-4 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={photo} alt={t.name} />
                        <AvatarFallback>{initials(t.name)}</AvatarFallback>
                      </Avatar>
                      <span className="rounded-xl bg-blue-500/10 p-2">
                        <Icon className="w-4 h-4 text-blue-600" />
                      </span>
                    </div>
                    <p className="mt-3 font-semibold text-foreground truncate">{t.name}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Badge variant="outline">Rank #{t.rank}</Badge>
                      <Badge className={statusBadge(t.result_status)} variant="outline">
                        {fmtPct(t.percentage)}
                      </Badge>
                      {t.grade ? (
                        <Badge className={gradePill(t.grade)} variant="outline">{t.grade}</Badge>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk actions */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-5 h-5" /> Bulk Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={publishAll} disabled={!canManage || headerActionsDisabled}>
            <Upload className="w-4 h-4 mr-2" /> Bulk Publish Results
          </Button>
          <Button variant="outline" onClick={exportPdf} disabled={headerActionsDisabled || bulkRunning}>
            <Download className="w-4 h-4 mr-2" /> Bulk Download PDFs
          </Button>
          <Button
            variant="outline"
            onClick={() => toast({ title: 'Not implemented', description: 'Export Excel can be added next.', variant: 'destructive' })}
          >
            Export Excel
          </Button>
          <Button
            variant="outline"
            onClick={() => toast({ title: 'Not implemented', description: 'Send to parents can be added next.', variant: 'destructive' })}
          >
            Send Results to Parents
          </Button>
          {bulkRunning && (
            <p className="text-sm text-muted-foreground ml-auto">
              Processing {progress.done} / {progress.total}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Results table */}
      <Card className="border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-5 h-5" /> Result Rankings
          </CardTitle>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span>{rows.length} student(s)</span>
            <Button size="sm" variant="outline" onClick={() => navigate(`/exams/${examId}/results`)}>
              {resultsEntryButtonLabel(exam)}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-xl border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="w-10">
                    <Checkbox checked={allSelected} onCheckedChange={(v) => toggleAll(Boolean(v))} />
                  </TableHead>
                  <TableHead>Rank</TableHead>
                  <TableHead>Roll Number</TableHead>
                  <TableHead>Student Name</TableHead>
                  <TableHead className="text-right">Percentage</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Result Status</TableHead>
                  <TableHead className="text-right">Attendance</TableHead>
                  <TableHead>Remarks</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-muted-foreground py-10">
                      No published results for current filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => {
                    const checked = selectedIds.includes(r.student_id);
                    const photo = getProfileAssetUrl(r.avatar, `${r.student_id}`) || '';
                    return (
                      <TableRow key={r.student_id} className="hover:bg-muted/20">
                        <TableCell>
                          <Checkbox checked={checked} onCheckedChange={() => toggleOne(r.student_id)} />
                        </TableCell>
                        <TableCell className="font-medium">{r.rank}</TableCell>
                        <TableCell>{r.roll_no || '—'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3 min-w-[220px]">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={photo} alt={r.student_name} />
                              <AvatarFallback>{initials(r.student_name)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-foreground">{r.student_name}</p>
                              <p className="text-xs text-muted-foreground">{r.admission_no || `#${r.student_id}`}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{fmtPct(r.percentage)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={gradePill(r.grade)}>{r.grade || '—'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={statusBadge(r.result_status)}>
                            {r.result_status === 'pass' ? 'Pass' : 'Fail'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{fmtPct(r.attendance_pct || 0)}</TableCell>
                        <TableCell className="max-w-[240px] truncate">{r.remarks || '—'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline" onClick={() => openPreview(r)}>View Result</Button>
                            <Button size="sm" variant="outline" onClick={() => downloadSinglePdf(r)}>
                              <Download className="w-4 h-4" />
                            </Button>
                            {canManage && (
                              <Button size="sm" variant="outline" onClick={publishSingle} title="Publish">
                                <Upload className="w-4 h-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => printSingle(r)}>
                              <Printer className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Preview modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Result Summary</DialogTitle>
            <DialogDescription>Student performance snapshot with subject marks and insights.</DialogDescription>
          </DialogHeader>

          {previewRow ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={getProfileAssetUrl(previewRow.avatar, `${previewRow.student_id}-preview`) || ''} alt={previewRow.student_name} />
                    <AvatarFallback>{initials(previewRow.student_name)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-lg font-semibold">{previewRow.student_name}</p>
                    <p className="text-sm text-muted-foreground">
                      Rank #{previewRow.rank} · {previewRow.admission_no || `#${previewRow.student_id}`} · Roll {previewRow.roll_no || '—'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className={statusBadge(previewRow.result_status)}>
                    {previewRow.result_status === 'pass' ? 'Pass' : 'Fail'}
                  </Badge>
                  <Badge variant="outline" className={gradePill(previewRow.grade)}>{previewRow.grade || '—'}</Badge>
                  <Badge variant="outline" className="bg-sky-500/10 text-sky-700 border-sky-500/20">
                    {fmtPct(previewRow.percentage)}
                  </Badge>
                  <Badge variant="outline">{fmtPct(previewRow.attendance_pct || 0)} attendance</Badge>
                </div>
              </div>

              <div className="rounded-xl border border-border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead>Subject</TableHead>
                      <TableHead className="text-right">Marks</TableHead>
                      <TableHead className="text-right">Max</TableHead>
                      <TableHead className="text-right">Passing</TableHead>
                      <TableHead>Grade</TableHead>
                      <TableHead>Remark</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(previewRow.subjects || []).map((s) => (
                      <TableRow key={`${previewRow.student_id}-${s.subject_id}-${s.subject_name}`}>
                        <TableCell className="font-medium">{s.subject_name}</TableCell>
                        <TableCell className="text-right">{s.marks_obtained}</TableCell>
                        <TableCell className="text-right">{s.max_marks}</TableCell>
                        <TableCell className="text-right">{s.passing_marks}</TableCell>
                        <TableCell>{s.grade_name || '—'}</TableCell>
                        <TableCell className="max-w-[260px] truncate">{s.remarks || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => downloadSinglePdf(previewRow)}>
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                <Button variant="outline" onClick={() => printSingle(previewRow)}>
                  <Printer className="w-4 h-4 mr-2" /> Print
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground">No preview selected.</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Result Settings</DialogTitle>
            <DialogDescription>Control analytics widgets shown on dashboard.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-border p-3">
              <div>
                <p className="font-medium">Grade distribution chart</p>
                <p className="text-sm text-muted-foreground">Show grade breakdown across students.</p>
              </div>
              <Switch checked={Boolean(settings.showGradeChart)} onCheckedChange={(v) => saveSettings({ ...settings, showGradeChart: Boolean(v) })} />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border p-3">
              <div>
                <p className="font-medium">Attendance vs performance</p>
                <p className="text-sm text-muted-foreground">Show scatter of attendance vs %.</p>
              </div>
              <Switch checked={Boolean(settings.showAttendanceChart)} onCheckedChange={(v) => saveSettings({ ...settings, showAttendanceChart: Boolean(v) })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSettingsOpen(false)}>Close</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setSettingsOpen(false)}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

